import { allColorShades, basicColorShades } from './core-shades'
import { createRelationObj } from './utils'

export const colors = {
	text: { dark: '#0D1E2C', light: '#FFFFFF' },
	warning: {
		main: '#FF9E0F',
		dark: '#EF6C00',
		hover: '#FB8C00',
		light: '#FFF7EB',
	},
	secondary: {
		main: '#DADCE0',
		dark: '#3240D7',
		yellow: '#FDE31A',
		hover: '#5263ff',
		white: '#FCFCFC',
	},
	ghost: {
		main: '#DADCE0',
		dark: '#636E72',
		light: '#F6F6F6',
		superLight: '#FCFCFC',
		placeholder: '#495458',
	},
	success: {
		main: '#32C387',
		dark: '#24A77E',
		light: '#ECFEEF',
		hover: '#2DAF78',
	},
	danger: {
		main: '#FF675F',
		dark: '#DB0700',
		light: '#FFEBED',
		hover: '#F4302A',
	},
	primary: {
		main: '#4299FF',
		dark: '#2167C9',
		light: '#ECF7FE',
		hover: '#0F7EFF',
		background: '#F5FBFE',
	},
	gradients: {
		rst: 'linear-gradient(135deg, #406F90 0%, #25B579 100%)',
	},
	bg: {
		main: '#F5F8FB',
	},
	info: { main: '' },
	gray: {
		main: '#D9D9D9',
	},
	neutral: {
		main: '#F5F5F5',
	},
}

const oldColorsArray = Object.entries(colors).reduce((a, [key, value]) => {
	const arr = []
	Object.entries(value).forEach(([k_, v_]) => {
		arr.push([v_, `${key}.${k_}`])
	})
	return [...a, ...arr]
}, [])

export const colorsSchemeNames = [
	'gray',
	'primary',
	'secondary',
	'success',
	'warning',
	'danger',
	'info',
	'violet',
	'purple',
	'yellow',
	'cyan',
]

export const colorsSchemeCore = allColorShades

export const ColorGradientsSchemeNames = {
	'gradient-breakfast': 'linear-gradient(90deg, #30CFD0cc 0%, #330867cc 100%)',
	'gradient-terrify': 'linear-gradient(90deg, #6A85B6cc 0%, #BAC8E0cc 100%)',
	'gradient-foreigner':
		'linear-gradient(90deg, #3F51B1cc 0%, #5A55AEcc 13%, #7B5FACcc 25%, #8F6AAEcc 38%, #A86AA4cc 50%, #CC6B8Ecc 62%, #F18271cc 75%, #F3A469cc 87%, #F7C978cc 100%)',
	'gradient-silver':
		'linear-gradient(90deg, #3D4E81cc 0%, #5753C9cc 48%, #6E7FF3cc 100%)',
	'gradient-city':
		'linear-gradient(90deg, #FF057Ccc 0%, #7C64D5cc 48%, #4CC3FFcc 100%)',
	'gradient-modest':
		'linear-gradient(90deg, #473B7Bcc 0%, #3584A7cc 51%, #30D2BEcc 100%)',
	'gradient-snake': 'linear-gradient(90deg, #B7F8DBcc 0%, #50A7C2cc 100%)',
	'gradient-agent': 'linear-gradient(90deg, #CBBACCcc 0%, #2580B3cc 100%)',
	'gradient-glow': 'linear-gradient(90deg, #EC77ABcc 0%, #7873F5cc 100%)',
	'gradient-fox': 'linear-gradient(90deg, #D7D2CCcc 0%, #304352cc 100%)',
	'gradient-professor': 'linear-gradient(90deg, #616161cc 0%, #9BC5C3cc 100%)',
	'gradient-trait':
		'linear-gradient(90deg, #3D3393cc 0%, #2B76B9cc 37%, #2CACD1cc 65%, #35EB93cc 100%)',
	'gradient-whatever': 'linear-gradient(90deg, #0ACFFEcc 0%, #495AFFcc 100%)',
	'gradient-decide': 'linear-gradient(90deg, #243949cc 0%, #517FA4cc 100%)',
	'gradient-area':
		'linear-gradient(90deg, #1E3C72cc 0%, #1E3C72cc 1%, #2A5298cc 100%)',
}

type OpacityGrayWhiteColorsType =
	| 'opacity-gray-01'
	| 'opacity-gray-02'
	| 'opacity-gray-03'
	| 'opacity-gray-04'
	| 'opacity-gray-05'
	| 'opacity-gray-06'
	| 'opacity-gray-07'
	| 'opacity-gray-08'
	| 'opacity-gray-09'
	| 'opacity-gray-10'
	| 'opacity-white-01'
	| 'opacity-white-02'
	| 'opacity-white-03'
	| 'opacity-white-04'
	| 'opacity-white-05'
	| 'opacity-white-06'
	| 'opacity-white-07'
	| 'opacity-white-08'
	| 'opacity-white-09'
	| 'opacity-white-10'
	| 'opacity-gray-to-white-0'
	| 'opacity-gray-to-white-01'
	| 'opacity-gray-to-white-02'
	| 'opacity-gray-to-white-03'
	| 'opacity-gray-to-white-04'
	| 'opacity-gray-to-white-05'
	| 'opacity-gray-to-white-06'
	| 'opacity-gray-to-white-07'
	| 'opacity-gray-to-white-08'
	| 'opacity-gray-to-white-09'
	| 'opacity-gray-to-white-10'

type OpacityColorShadesType =
	| `opacity-${keyof typeof basicColorShades}`
	| OpacityGrayWhiteColorsType

export type ColorsSchemeNames = keyof typeof colorsSchemeCore

export type FullColorNamesType = ColorsSchemeNames | OpacityColorShadesType

export type ColorsSchemeCoreNames =
	| 'dark'
	| 'primary'
	| 'secondary'
	| 'success'
	| 'danger'
	| 'warning'
	| 'info'
	| 'ghost'
	| 'violet'
	| 'purple'
	| 'yellow'
	| 'cyan'
	| 'gray'
	| 'neutral'
export type ColorsSchemeType = typeof colorsSchemeCore

export const colorsList = [
	'primary',
	'secondary',
	'success',
	'danger',
	'warning',
	'info',
	'violet',
	'purple',
	'yellow',
	'cyan',
	'gray',
] as const

const createColorMap = () => {
	return colorsList.reduce((a, c) => {
		return [
			...a,
			[c, `${c}-08`],
			[`${c}.main`, `${c}-08`],
			[`${c}.hover`, `${c}-09`],
			[`${c}.dark`, `${c}-10`],
			[`${c}.light`, `${c}-02`],
		]
	}, [] as [from: string, to: string][])
}

export const relations: [from: string, to: string][] = [
	['text.dark', 'gray-20'],
	['text.light', 'gray-04'],

	['primary.background', 'primary-03'],

	...createColorMap(),
	...oldColorsArray,

	['ghost', 'gray-08'],
	['ghost.main', 'gray-08'],
	['ghost.dark', 'gray-12'],
	['ghost.light', 'gray-06'],
	['ghost.superLight', 'gray-06'],
	['ghost.placeholder', 'gray-14'],

	['background', 'gray-02'],
	['placeholder', 'gray-14'],
	['border', 'gray-08'],
	['text', 'gray-20'],
	['black', 'gray-19'],
	['white', 'gray-04'],
]
export const relationsMain: [from: string, to: string][] = [
	...relations,
	['dark', 'gray-20'],
	['secondary', `violet-08`],
	[`secondary.main`, `violet-08`],
	[`secondary.placeholder`, `violet-09`],
	[`secondary.dark`, `violet-10`],
	[`secondary.light`, `violet-02`],
]

export const colorsScheme = createRelationObj(colorsSchemeCore, relationsMain)
